import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion, useInView } from "framer-motion";
import { useMediaQuery } from "@mui/material";

const checkIcon = <CheckCircleIcon color="primary" sx={{ mr: 1 }} />;

export default function Integration() {
  const insightsRef = useRef(null);
  const integrationRef = useRef(null);
  const restaurantRef = useRef(null);
  const [ismedium, setIsmedium] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    const checkWidth = () => {
      setIsmedium(window.innerWidth === 768);
    };
    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  const inViewInsights = useInView(insightsRef, { once: true, amount: 0.2 });
  const inViewIntegration = useInView(integrationRef, {
    once: true,
    amount: 0.2,
  });
  const inViewRestaurant = useInView(restaurantRef, {
    once: true,
    amount: 0.2,
  });

  const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
  };

  return (
    <Box
      sx={{
        backgroundImage: 'url("/assets/reservation/workbg.svg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: "#fff",
        borderRadius: 3,
        mt: 4,
        py: { xs: 4, sm: 4 },
        px: { xs: 2, sm: 4, md: 10 },
        mx: { xs: 2, sm: 2 },
      }}
    >
      <Box mb={{ xs: 2, sm: 6 }} ref={insightsRef}>
        <Typography variant="h1" sx={{ color: "#F68528", fontWeight: 700 }}>
          Insights That Drive
          {isMobile ? " " : <br />}
          Growth
        </Typography>
        <Grid container spacing={3} alignItems="center" mt={{ xs: 0, sm: 2 }}>
          <Grid item xs={12} md={4}>
            <motion.img
              src="/assets/features/pos/integration1.png"
              alt="Insights"
              variants={fadeInLeft}
              initial="hidden"
              animate={inViewInsights ? "visible" : "hidden"}
              style={ismedium ? { width: "100%" } : {}}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <motion.div
              variants={fadeInRight}
              initial="hidden"
              animate={inViewInsights ? "visible" : "hidden"}
            >
              <Typography mb={2} variant="h6" sx={{ fontWeight: 600 }}>
                Egora POS goes beyond order processing. It provides clear
                insights to help you make better decisions.
              </Typography>
              <List>
                {[
                  "Generate daily, weekly, and monthly sales reports.",
                  "Identify best-selling items and categories.",
                  "Track revenue and expenses.",
                  "Review operational performance with built-in reporting tools.",
                ].map((text) => (
                  <ListItem key={text}>
                    <ListItemIcon>{checkIcon}</ListItemIcon>
                    <ListItemText primary={text} />
                  </ListItem>
                ))}
              </List>
            </motion.div>
          </Grid>
        </Grid>
      </Box>

      <Box mb={{ xs: 0, sm: 6 }} ref={integrationRef}>
        <Typography variant="h1" sx={{ color: "#F68528", fontWeight: 700 }}>
          Seamless Integration {isMobile ? " " : <br />} with Egora Ecosystem
        </Typography>
        <Grid container spacing={3} alignItems="start" mt={{ xs: 0, sm: 6 }}>
          <Grid item xs={12} md={6}>
            <motion.div
              variants={fadeInLeft}
              initial="hidden"
              animate={inViewIntegration ? "visible" : "hidden"}
            >
              <Typography mb={2} variant="h6" sx={{ fontWeight: 600 }}>
                Egora POS works smoothly with other modules in the <br /> Egora
                platform, including:
              </Typography>
              <List>
                {[
                  "Kitchen Display System",
                  "Reservation Engine",
                  "QR Code Ordering",
                  "Inventory and Warehouse Management",
                  "Customer Engagement and CRM",
                  "In-House Delivery Management",
                  "Financial Ledger and Reporting",
                ].map((text) => (
                  <ListItem key={text}>
                    <ListItemIcon>{checkIcon}</ListItemIcon>
                    <ListItemText primary={text} />
                  </ListItem>
                ))}
              </List>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.img
              src="/assets/features/pos/integration2.svg"
              alt="POS Integration"
              variants={fadeInRight}
              initial="hidden"
              animate={inViewIntegration ? "visible" : "hidden"}
              style={ismedium ? { width: "100%" } : {}}
            />
          </Grid>
        </Grid>
      </Box>

      <Box ref={restaurantRef}>
        <Typography variant="h1" sx={{ color: "#F68528", fontWeight: 700 }}>
          Built for All Types of {isMobile ? " " : <br />} Restaurants
        </Typography>
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} md={6}>
            <motion.img
              src="/assets/features/pos/integration3.png"
              alt="POS Integration"
              variants={fadeInLeft}
              initial="hidden"
              animate={inViewRestaurant ? "visible" : "hidden"}
              style={ismedium ? { width: "100%" } : {}}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              variants={fadeInRight}
              initial="hidden"
              animate={inViewRestaurant ? "visible" : "hidden"}
            >
              <Typography mb={2} variant="h6" sx={{ fontWeight: 600 }}>
                Egora POS supports:
              </Typography>
              <List>
                {[
                  "Fine dining restaurants",
                  "Quick service restaurants",
                  "Cafes and coffee shops",
                  "Bakeries",
                  "Food trucks",
                  "Cloud kitchens",
                ].map((text) => (
                  <ListItem key={text}>
                    <ListItemIcon>{checkIcon}</ListItemIcon>
                    <ListItemText primary={text} />
                  </ListItem>
                ))}
              </List>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
